import React from "react";
import { formatDay } from "../api/shape.js";
import { NOT_ATTENDED_MAX } from "../api/endOfDay.js";
import { captureUrlFor as defaultCaptureUrlFor } from "../../../config.js";
import { usePhone } from "../phone/usePhone.js";
import PhoneEndOfDay from "../phone/PhoneEndOfDay.jsx";
import { useEndOfDay } from "./day/useEndOfDay.js";

/**
 * End of day (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 2.1; mockups/cockpit-v3/
 * EndOfDay.dc.html, phone: mobile/EndOfDay.dc.html). `model` is
 * api/endOfDay.js#shapeEndOfDay's output (live and fixture alike).
 *
 * The site manager closes every open side (carry, record now, not attended),
 * keeps or carries the postponed sides, and approves the day's records with one
 * submit. Approving is the sign-off for the client.
 */

export const EOD_FOOTER =
  "Approving is your sign-off for the client: the weekly report counts approved records only. Untick a record to leave it open; the inspector can still change it. A record with a defect noted becomes a repair check for that block, item and side.";

function chipClass(tone) {
  return `chip ${tone === "ok" ? "ok" : tone === "warn" ? "warn" : tone === "bad" ? "bad" : "mute"}`;
}

function ReasonPanel({ side, state }) {
  const id = `eod-reason-${side.scheduleRef}`;
  return (
    <div className="eod-reason" data-testid="eod-reason" role="group" aria-label={`Not attended: ${side.title}`}>
      <label htmlFor={id} className="sub">Why was this not attended? (1 to {NOT_ATTENDED_MAX} characters)</label>
      <textarea
        id={id}
        className="eod-reason-input"
        rows={2}
        maxLength={NOT_ATTENDED_MAX + 50}
        value={state.reasonText}
        onChange={(e) => state.setReasonText(e.target.value)}
      />
      {state.reasonError ? <span className="day-msg" role="alert" data-testid="eod-reason-error">{state.reasonError}</span> : null}
      <div className="acts">
        <button type="button" className="btn" onClick={state.closeReason}>Cancel</button>
        <button type="button" className="btn fill" disabled={state.pendingKey === side.key} onClick={() => state.confirmReason(side)}>Mark not attended</button>
      </div>
    </div>
  );
}

function SideRow({ side, model, state, captureUrlFor }) {
  const busy = state.pendingKey === side.key;
  return (
    <>
      <div className="r" data-testid="eod-side" data-state="to_go" data-side-ref={side.scheduleRef}>
        <span className="time">{side.timeText}</span>
        <div className="eod-text">
          <span className="t1">{side.title}</span>
          <span className="sub">{[side.place, side.who].filter(Boolean).join(" · ")}</span>
        </div>
        <div className="acts">
          <button type="button" className="btn" disabled={busy || state.submitting} onClick={() => state.carry(side)}>{busy ? "…" : "Carry to tomorrow"}</button>
          <a className="btn eod-link" href={captureUrlFor(side.projectCode)} target="_blank" rel="noreferrer">Record now</a>
          <button type="button" className="btn" disabled={busy || state.submitting} onClick={() => state.openReason(side)}>Not attended…</button>
        </div>
      </div>
      {state.reasonKey === side.key ? <ReasonPanel side={side} state={state} /> : null}
    </>
  );
}

function PostponedRow({ side, model, state }) {
  const keep = state.kept.has(side.key);
  return (
    <div className="r" data-testid="eod-postponed" data-keep={keep ? "true" : "false"} data-side-ref={side.sides.map((x) => x.scheduleRef).join(" ")}>
      <span className="time">{side.timeText}</span>
      <div className="eod-text">
        <span className="t1">{side.title}</span>
        <span className="sub">{["Postponed by the yard", side.who].join(" · ")}</span>
      </div>
      <div className="acts">
        <span className={`chip ${keep ? "mute" : "warn"}`}>{keep ? "KEPT ON TODAY" : model.carryLabel.toUpperCase()}</span>
        <button type="button" className="btn" onClick={() => state.toggleKeep(side.key)}>{keep ? `Carry to ${model.carryLabel}` : "Keep on today"}</button>
      </div>
    </div>
  );
}

function ClosedRow({ side, model }) {
  const text = side.state === "carried" ? `Carried to ${side.carriedTo ? formatDay(`${side.carriedTo}T00:00:00Z`) : model.carryLabel}` : `Not attended: ${side.notAttendedReason}`;
  return (
    <div className="r" data-testid="eod-closed" data-state={side.state}>
      <span className="time">{side.timeText}</span>
      <div className="eod-text">
        <span className="t1">{side.title}</span>
        <span className="sub">{text}</span>
      </div>
      <div className="acts"><span className="chip mute">{side.state === "carried" ? "CARRIED" : "NOT ATTENDED"}</span></div>
    </div>
  );
}

function RecordRow({ rec, state }) {
  const ticked = !state.unticked.has(rec.key);
  return (
    <div className="g" data-testid="eod-record" data-ticked={ticked ? "true" : "false"} data-inspection={rec.inspectionNumber}>
      <span><input type="checkbox" checked={ticked} onChange={() => state.toggleRecord(rec.key)} aria-label={`Approve ${rec.title} ${rec.sideText}`} /></span>
      <span className="time">{rec.time}</span>
      <span className="t1">{rec.title}</span>
      <span className="mono t1">{rec.sideText}</span>
      <span className={chipClass(rec.resultTone)} style={{ justifySelf: "start" }}>{rec.result}</span>
      <span className="eod-defect" style={{ color: rec.defectNoted ? "var(--warn)" : "var(--t3)" }}>{rec.defect}</span>
      <span className={rec.photoCount ? "mono sub" : "chip bad"} style={{ justifySelf: "start" }}>{rec.photoCount}</span>
      <span className="sub">{rec.capturedBy}</span>
    </div>
  );
}

export default function EndOfDay({ model, errors = [], onBack, onCloseSide, onSubmit, captureUrlFor = defaultCaptureUrlFor, ...rest }) {
  const phone = usePhone();
  const state = useEndOfDay({ model, onCloseSide, onSubmit });
  if (phone) {
    return <PhoneEndOfDay model={model} errors={errors} state={state} onBack={onBack} captureUrlFor={captureUrlFor} {...rest} />;
  }
  const s = model.summary;
  return (
    <div className="day-page eod-page" data-testid="end-of-day" data-day-date={model.date}>
      <div className="day-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          <button type="button" className="eod-back" data-testid="eod-back" onClick={onBack}>Back to Today</button>
          <div className="h1">End of day{model.dateLabel ? ` · ${model.dateLabel}` : ""}</div>
          <div className="day-figures" data-testid="eod-summary">
            <span className="req" style={{ fontSize: 22 }}>{s.recordedText}</span>
            {s.postponedText ? <span style={{ fontSize: 14, fontWeight: 600, color: "var(--warn)" }}>{s.postponedText}</span> : null}
            {s.unscheduledText ? <span style={{ fontSize: 14, fontWeight: 600, color: "var(--t2)" }}>{s.unscheduledText}</span> : null}
            {s.noResultText ? <span style={{ fontSize: 14, fontWeight: 600, color: "var(--bad)" }}>{s.noResultText}</span> : null}
            {s.closedText ? <span style={{ fontSize: 14, fontWeight: 600, color: "var(--t2)" }}>{s.closedText}</span> : null}
          </div>
        </div>
      </div>

      {errors.length > 0 ? (
        <div className="list" data-testid="eod-errors">
          {errors.map((e) => (
            <div className="r" key={e.code} style={{ gridTemplateColumns: "1fr" }}>
              <span className="ttl" style={{ color: "var(--bad)" }}>{e.code}: this day is unavailable &mdash; {e.error}</span>
            </div>
          ))}
        </div>
      ) : null}

      {state.message ? (
        <div className="day-msg" role="alert" data-testid="eod-message" data-tone={state.message.tone} style={{ color: state.message.tone === "ok" ? "var(--ok)" : "var(--warn)" }}>{state.message.text}</div>
      ) : null}

      {!model.available ? (
        <div className="day-empty" data-testid="eod-unavailable">
          <h2>This day is not available</h2>
          <p>The day could not be read. Go back to Today and try again.</p>
        </div>
      ) : null}

      {model.available && model.noResult.length > 0 ? (
        <section className="day-lane" data-testid="eod-section-noresult">
          <h2 className="sec" style={{ color: "var(--bad)" }}><span>Requested by the yard, no result recorded</span></h2>
          <div className="list">
            {model.noResult.map((side) => <SideRow key={side.key} side={side} model={model} state={state} captureUrlFor={captureUrlFor} />)}
          </div>
        </section>
      ) : null}

      {model.available && model.postponed.length > 0 ? (
        <section className="day-lane" data-testid="eod-section-postponed">
          <h2 className="sec warn"><span>Postponed · carried to {model.carryLabel}</span></h2>
          <div className="list">
            {model.postponed.map((side) => <PostponedRow key={side.key} side={side} model={model} state={state} />)}
          </div>
        </section>
      ) : null}

      {model.available && model.closed.length > 0 ? (
        <section className="day-lane" data-testid="eod-section-closed">
          <h2 className="sec"><span>Already closed</span><span className="mono">{model.closed.length}</span></h2>
          <div className="list">
            {model.closed.map((side) => <ClosedRow key={side.key} side={side} model={model} />)}
          </div>
        </section>
      ) : null}

      {model.available ? (
        <section className="day-lane" data-testid="eod-section-records">
          <div className="eod-recordshead">
            <h2 className="sec" style={{ flex: 1, color: "var(--t1)" }}><span>Records to approve · {model.records.length}</span></h2>
            {model.records.length > 0 ? (
              <span className="sub" style={{ whiteSpace: "normal" }}>
                {model.withDefects} with defects · {model.withoutPhotos} without photos{model.withoutRequest ? ` · ${model.withoutRequest} without a yard request` : ""}
              </span>
            ) : null}
            <button type="button" className="btn fill" data-testid="eod-approve" disabled={!state.canSubmit} onClick={state.submit}>{state.submitting ? "Approving…" : state.submitLabel}</button>
          </div>
          {model.records.length > 0 ? (
            <div className="list">
              <div className="g head">
                <span><input type="checkbox" checked={state.allTicked} onChange={state.toggleAll} aria-label="Select all records" /></span>
                <span>Time</span><span>Block and item</span><span>Side</span><span>Result</span><span>Defects</span><span>Photos</span><span>Recorded by</span>
              </div>
              {model.records.map((rec) => <RecordRow key={rec.key} rec={rec} state={state} />)}
            </div>
          ) : (
            <div className="list"><div className="r" style={{ gridTemplateColumns: "1fr" }}><span className="sub" data-testid="eod-no-records">{model.approvedCount ? `All ${model.approvedCount} records of this day are approved.` : "No records on this day."}</span></div></div>
          )}
          <p className="day-foot" data-testid="eod-footer">{EOD_FOOTER}</p>
        </section>
      ) : null}
    </div>
  );
}
